import React, { createContext, useContext } from "react";
import { mdiPlusCircleOutline, mdiPencil } from "@mdi/js";
import { cn, getFocusRingStyles } from "../../styles";
import {
  Button,
  ButtonVariant,
  Typography,
  TypographyContent,
} from "../../atoms";
import { DragItem } from "../../molecules";
import {
  DragList,
  DragListItemProps,
  DragListProps,
  ScrollArea,
} from "../../layouts";
import { ThemeColor, BaseElementProps } from "../../props";

const EditingContext = createContext(false);

export type AppBarLinkRenderProp = (props: {
  editing: boolean;
  className: string;
}) => React.ReactNode;

export interface AppBarLinkProps<T>
  extends
    BaseElementProps,
    Partial<Omit<DragListItemProps<T>, "children" | "data">> {
  children: AppBarLinkRenderProp;
  dragData?: T;
  active?: boolean;
  onEdit?: () => void;
}

export function AppBarLink<T = unknown>({
  className,
  active = false,
  onEdit,
  id,
  index,
  children,
  dragData,
}: AppBarLinkProps<T>) {
  const editing = useContext(EditingContext);
  if (editing)
    return (
      <DragList.Item id={`pss-appbar-link-${id}`} index={index} data={dragData}>
        {(dragProps) => (
          <DragItem
            {...dragProps}
            className={cn("dark:!bg-back-700")}
            onAction={onEdit}
            buttonProps={{ iconPath: mdiPencil }}
            dense
          >
            <div className="grow p-1 text-xs font-normal flex items-center">
              {children({ editing, className: "" })}
            </div>
          </DragItem>
        )}
      </DragList.Item>
    );
  return (
    <>
      <div className="first:hidden h-4 w-px bg-back-300 dark:bg-back-600" />
      {children({
        editing,
        className: cn(
          "flex items-center text-front-900 dark:text-front-100 hover:bg-back-200 dark:hover:bg-back-700 px-4 py-2 rounded-md",
          "p-2 outline-none focus-visible:ring-2 rounded",
          getFocusRingStyles(ThemeColor.primary),
          active && "underline",
          className,
        ),
      })}
    </>
  );
}

export interface AppBarNavigationProps<T>
  extends BaseElementProps, Partial<DragListProps<T>> {
  children: React.ReactNode;
  onAddLink: () => void;
  addLink: TypographyContent;
  editing?: boolean;
}

export function AppBarNavigation<T = unknown>({
  className,
  children,
  onAddLink,
  onDragListChange,
  id = "pss-appbar-nav",
  type = "pss-appbar-nav",
  addLink = "AddLink",
  editing = false,
}: AppBarNavigationProps<T>) {
  if (editing)
    return (
      <div className="h-full min-w-0 flex items-center px-6 space-x-4">
        <ScrollArea
          direction="horizontal"
          className="h-full flex-1"
          contentClassName="h-full"
        >
          <DragList
            id={id}
            className="h-full flex items-center flex-1 space-x-3"
            type={type}
            direction="horizontal"
            onDragListChange={onDragListChange}
          >
            <EditingContext.Provider value={true}>
              {children}
            </EditingContext.Provider>
          </DragList>
        </ScrollArea>
        {onAddLink && (
          <Button
            variant={ButtonVariant.outlined}
            color={ThemeColor.gray}
            muted
            iconPath={mdiPlusCircleOutline}
            onClick={onAddLink}
          >
            <Typography.Span content={addLink} />
          </Button>
        )}
      </div>
    );
  return (
    <ScrollArea
      direction="horizontal"
      className={cn("h-full min-w-0 px-6", className)}
      contentClassName="h-full"
    >
      <nav className="h-full flex items-center gap-0.5 text-sm">{children}</nav>
    </ScrollArea>
  );
}
